// src/components/EventsCalendar.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  orderBy
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom'; 
import { useTranslation } from 'react-i18next'; // <-- MODIFICA: Importa hook

// --- IMPORT MUI (Invariati) ---
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Paper,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EventIcon from '@mui/icons-material/Event';

// --- MODIFICA FORMATO DATA (ora usa la lingua corrente) ---
const formatDate = (date, lang) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    // Usa 'it-IT' per italiano, 'en-GB' per inglese (formato DD/MM/YYYY)
    const locale = lang === 'it' ? 'it-IT' : 'en-GB'; 
    return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
};
// --- FINE MODIFICA ---

function EventsCalendar({ teamId }) {
  // --- MODIFICA: Inizializza t() ---
  const { t, i18n } = useTranslation('event');
  const currentLang = i18n.language;
  // --- FINE MODIFICA ---

  // Stati (invariati)
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [eventName, setEventName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tracksList, setTracksList] = useState([]);
  const [isLoadingTracks, setIsLoadingTracks] = useState(true);
  const [selectedTrack, setSelectedTrack] = useState(null); 

  // useEffects (invariati)
  useEffect(() => {
    if (!teamId) { setIsLoading(false); return; }
    setIsLoading(true);
    const eventsQuery = query(
      collection(db, "events"), where("teamId", "==", teamId), orderBy("startDate", "desc")
    );
    const unsubscribe = onSnapshot(eventsQuery, (qs) => {
      setEvents(qs.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setIsLoading(false);
    }, (err) => { console.error("Error (index needed?):", err.message); setIsLoading(false); });
    return () => unsubscribe();
  }, [teamId]);

  useEffect(() => {
    if (!teamId) { setIsLoadingTracks(false); return; }
    setIsLoadingTracks(true);
    const tracksQuery = query(
      collection(db, "tracks"),
      where("teamId", "==", teamId),
      orderBy("nome", "asc")
    );
    const unsubscribe = onSnapshot(tracksQuery, (qs) => {
      setTracksList(qs.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setIsLoadingTracks(false);
    }, (err) => {
      console.error("Error loading tracks:", err);
      setIsLoadingTracks(false);
    });
    return () => unsubscribe();
  }, [teamId]);

  // --- MODIFICA: Funzioni Handler (con t() per alert) ---
  const handleAddEvent = async (e) => {
    e.preventDefault();
    if (!eventName || !startDate || !endDate) { 
      alert(t('error_all_fields')); // Tradotto
      return; 
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    start.setMinutes(start.getMinutes() + start.getTimezoneOffset());
    end.setMinutes(end.getMinutes() + end.getTimezoneOffset());
    if (end < start) { 
      alert(t('error_date_order')); // Tradotto
      return; 
    }

    setIsSubmitting(true);
    try {
      await addDoc(collection(db, "events"), {
        teamId: teamId,
        eventName: eventName.trim(),
        trackName: selectedTrack ? selectedTrack.nome : '',
        trackId: selectedTrack ? selectedTrack.id : null,
        startDate: start,
        endDate: end,
        createdAt: serverTimestamp()
      });
      setEventName(''); setStartDate(''); setEndDate(''); setSelectedTrack(null);
    } catch (error) { 
      console.error("Error:", error); 
      alert(t('error_generic')); // Tradotto
    }
    setIsSubmitting(false);
  };

  const handleDeleteEvent = async (e, eventId) => {
    e.stopPropagation();
    if (window.confirm(t('delete_confirm'))) { // Tradotto
        try { await deleteDoc(doc(db, "events", eventId)); }
        catch (error) { 
          console.error("Error:", error); 
          alert(t('error_generic')); // Tradotto
        }
    }
  };
  // --- FINE MODIFICA ---

  // --- RENDER (MODIFICATO con t()) ---
  return (
    <>
      {/* 1. Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          {t('calendar_title')}
        </Typography>
      </Paper>

      {/* 2. Paper Form Aggiungi Evento */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2, sm: 3 }, 
          mb: 3,
          border: '1px solid', 
          borderColor: 'divider', 
          backgroundColor: 'background.paper' 
        }}
      >
        <Box component="form" onSubmit={handleAddEvent}>
          <Typography variant="h5" gutterBottom>{t('add_title')}</Typography>
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, mb: 2.5 }}>
            <TextField 
              label={t('event_name_label')}
              value={eventName} 
              onChange={(e) => setEventName(e.target.value)} 
              required 
              fullWidth
              sx={{ flex: 1 }}
            />
            <Autocomplete
              options={tracksList}
              getOptionLabel={(option) => option.nome || ''}
              value={selectedTrack}
              onChange={(event, newValue) => { setSelectedTrack(newValue); }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={isLoadingTracks}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('track_label')}
                  fullWidth
                />
              )}
              sx={{ flex: 1 }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, alignItems: 'center' }}>
            <TextField 
              label={t('start_date_label')}
              type="date" 
              value={startDate} 
              onChange={(e) => setStartDate(e.target.value)} 
              required 
              fullWidth 
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            <TextField 
              label={t('end_date_label')}
              type="date" 
              value={endDate} 
              onChange={(e) => setEndDate(e.target.value)} 
              required 
              fullWidth 
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            <Button 
              type="submit" 
              variant="contained" 
              disabled={isSubmitting || isLoadingTracks} 
              startIcon={isSubmitting ? <CircularProgress size={20} /> : <AddIcon />} 
              size="large" 
              sx={{ height: '56px', width: { xs: '100%', md: 'auto' }, flex: 1 }} 
            >
              {t('add_button')}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* 3. Paper Lista Eventi */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2, sm: 3 },
          border: '1px solid', 
          borderColor: 'divider', 
          backgroundColor: 'background.paper' 
        }}
      >
        <Typography variant="h5" gutterBottom>{t('list_title')}</Typography>
        {isLoading && <CircularProgress size={24} />}
        {!isLoading && events.length === 0 && ( <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{t('empty')}</Typography> )}
        
        {!isLoading && events.length > 0 && (
          <List>
            {events.map((event) => (
              <ListItem
                key={event.id}
                disablePadding
                divider
                sx={{ 
                  backgroundColor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  mb: 1
                }}
                secondaryAction={
                  <IconButton edge="end" aria-label="delete event" onClick={(e) => handleDeleteEvent(e, event.id)} sx={{ mr: 1 }}>
                    <DeleteIcon color="error" />
                  </IconButton>
                }
              >
                <ListItemButton onClick={() => navigate(`/event/${event.id}`)} sx={{ borderRadius: 2 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <EventIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={event.eventName}
                    primaryTypographyProps={{ fontWeight: 600 }}
                    secondary={
                      `${t('list.from')} ${formatDate(event.startDate, currentLang)} ${t('list.to')} ${formatDate(event.endDate, currentLang)}` +
                      `${event.trackName ? ` - ${t('list.track')} ${event.trackName}` : ''}`
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </>
  );
}

export default EventsCalendar;